import React, { useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { KnockoutBracket } from '@/components/competition/KnockoutBracket';
import { WcGroups365 } from '@/components/competition/WcGroups365';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { Skeleton } from '@/components/ui/Skeleton';
import { useScores365Standings } from '@/features/competition/hooks';

type WcTab = 'groups' | 'bracket';

/**
 * The richer World Cup view: 365scores group standings (FIFA rank, recent form,
 * qualification destinations) and the live knockout bracket, behind a two-tab
 * switcher. Standalone screen reached from the Today hub / Leagues — it never
 * touches the API-Football standings the Leagues tab renders.
 */
export default function WcGroupsScreen() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<WcTab>('groups');

  const standingsQ = useScores365Standings();

  const hasGroups = (standingsQ.data?.groups ?? []).some((g) => g.rows.length > 0);

  return (
    <Screen>
      <BackHeader title={t('wcGroups.title')} />
      <View className="px-4 pb-3 pt-1">
        <Segmented
          options={[
            { value: 'groups', label: t('wcGroups.tabGroups'), icon: 'grid-outline' },
            { value: 'bracket', label: t('wcGroups.tabBracket'), icon: 'git-network-outline' },
          ]}
          value={tab}
          onChange={setTab}
        />
      </View>

      {tab === 'groups' ? (
        standingsQ.isPending ? (
          <View className="gap-4 px-4 pt-1">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-44 w-full" />
            ))}
          </View>
        ) : hasGroups ? (
          // WcGroups365 IS the vertical scroller here (a FlatList), so we never
          // nest a VirtualizedList inside a plain ScrollView. The subtitle rides
          // along as the list header.
          <WcGroups365
            contentContainerStyle={{ paddingBottom: 48, paddingTop: 4 }}
            ListHeaderComponent={
              <AppText variant="micro" className="mb-3 leading-5 text-muted">
                {t('wcGroups.groupsSubtitle')}
              </AppText>
            }
          />
        ) : (
          <EmptyState
            icon="podium-outline"
            title={t('leagues.noStandingsTitle')}
            body={t('leagues.noStandingsBody')}
          />
        )
      ) : (
        // The bracket owns its own scroller, subtitle, skeleton and empty state;
        // a hair of top padding keeps its first round header off the segmented
        // control so the road-to-final reads as a deliberately framed tree.
        <View className="flex-1 pt-1">
          <KnockoutBracket />
        </View>
      )}
    </Screen>
  );
}
